import Cookie from 'js-cookie'
import { generateUUID } from '../utils/uuid'
import { getCookieDomain, isSecureContext } from '../utils/cookie-domain'
import {
  COOKIE_NAME,
  IDENTITY_COOKIE_NAME,
  IDENTITY_DATA_COOKIE_NAME,
  IDENTITY_STORAGE_KEY,
} from '../constants'

export interface IdentitySnapshot {
  anonymousId: string
  userId: string | null
  traits: Record<string, unknown>
}

interface PersistedIdentity {
  userId?: string
  email?: string
  phone?: string
  traits?: Record<string, unknown>
}

/**
 * Persist the lean identity (`userId`, `email`, `phone`) to a root-domain
 * cookie so it survives cross-subdomain navigation. Undefined fields are
 * omitted to keep the cookie small. js-cookie URL-encodes the JSON value.
 */
function writeIdentityCookie(data: PersistedIdentity): void {
  const lean: PersistedIdentity = {}
  if (data.userId) lean.userId = data.userId
  if (data.email) lean.email = data.email
  if (data.phone) lean.phone = data.phone

  // Nothing worth persisting (e.g. anonymous reset) — drop the cookie instead
  // of writing an empty object.
  if (!lean.userId && !lean.email && !lean.phone) {
    clearIdentityCookie()
    return
  }

  Cookie.set(IDENTITY_DATA_COOKIE_NAME, JSON.stringify(lean), {
    secure: isSecureContext(),
    sameSite: 'Lax',
    path: '/',
    domain: getCookieDomain(),
    expires: 365,
  })
}

function readIdentityCookie(): PersistedIdentity | null {
  const raw = Cookie.get(IDENTITY_DATA_COOKIE_NAME)
  if (!raw) return null
  try {
    return JSON.parse(raw) as PersistedIdentity
  } catch {
    return null
  }
}

function clearIdentityCookie(): void {
  Cookie.remove(IDENTITY_DATA_COOKIE_NAME, { path: '/', domain: getCookieDomain() })
}

/**
 * One-time migration source: the former localStorage store. Read-only — we no
 * longer write here. Lets already-identified visitors keep their identity when
 * they first load a build that persists to {@link IDENTITY_DATA_COOKIE_NAME}.
 */
function readLegacyLocalStorage(): PersistedIdentity | null {
  try {
    const raw = localStorage.getItem(IDENTITY_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PersistedIdentity
  } catch {
    return null
  }
}

function clearLegacyLocalStorage(): void {
  try {
    localStorage.removeItem(IDENTITY_STORAGE_KEY)
  } catch {
    // noop
  }
}

class IdentityStoreImpl {
  private anonymousId: string
  private userId: string | null = null
  private traits: Record<string, unknown> = {}

  constructor() {
    this.anonymousId = this.restoreAnonymousId()
    this.restoreIdentity()
  }

  getAnonymousId(): string {
    return this.anonymousId
  }

  getUserId(): string | null {
    return this.userId
  }

  getTraits(): Record<string, unknown> {
    return { ...this.traits }
  }

  getSnapshot(): IdentitySnapshot {
    return {
      anonymousId: this.anonymousId,
      userId: this.userId,
      traits: { ...this.traits },
    }
  }

  setIdentity(userId?: string, traits?: Record<string, unknown>): void {
    if (userId) {
      this.userId = userId
      Cookie.set(IDENTITY_COOKIE_NAME, userId, {
        secure: isSecureContext(),
        sameSite: 'Lax',
        path: '/',
        domain: getCookieDomain(),
        expires: 365,
      })
    }

    if (traits) {
      this.traits = { ...this.traits, ...traits }
      if (traits.email) this.traits.email = traits.email
      if (traits.phone) this.traits.phone = traits.phone
    }

    // Persist only the principal keys (userId/email/phone) to the root-domain
    // cookie. Richer traits remain in `this.traits` for the live session but
    // are intentionally NOT persisted.
    writeIdentityCookie({
      userId: this.userId ?? undefined,
      email: this.traits.email as string | undefined,
      phone: this.traits.phone as string | undefined,
    })
  }

  reset(): void {
    // Clear persisted identity
    Cookie.remove(IDENTITY_COOKIE_NAME, { path: '/', domain: getCookieDomain() })
    clearIdentityCookie()
    clearLegacyLocalStorage()

    // Generate new anonymous ID
    this.anonymousId = this.generateAndPersistAnonymousId()
    this.userId = null
    this.traits = {}
  }

  /**
   * Backward-compatible: returns the anonymous ID as the visitor hash.
   * Used by FaroDestination for `user.hash`.
   */
  captureVisitorId(): string {
    return this.anonymousId
  }

  private restoreAnonymousId(): string {
    const cookie = Cookie.get(COOKIE_NAME)
    if (cookie) return cookie

    return this.generateAndPersistAnonymousId()
  }

  private generateAndPersistAnonymousId(): string {
    const id = generateUUID()
    Cookie.set(COOKIE_NAME, id, {
      secure: isSecureContext(),
      sameSite: 'Strict',
      path: '/',
      domain: getCookieDomain(),
      // Sem `expires` isto seria um cookie de SESSÃO: morreria ao fechar o
      // navegador e o mesmo visitante voltaria com um hash novo. A janela
      // acompanha `_montilla_uid` / `_montilla_idt` (365) porque os três
      // descrevem o MESMO visitante — o hash não pode expirar antes do userId
      // que o acompanha, nem antes da atribuição de ads (90 dias).
      //
      // 365 é o pedido, não a garantia: navegadores Chromium truncam qualquer
      // janela acima de 400 dias, e o ITP do Safari apaga cookie gravado por
      // `document.cookie` após 7 dias sem visita ao site. Nenhum valor aqui
      // muda isso — persistir mais no Safari exigiria `Set-Cookie` vindo do
      // servidor, que um SDK client-side não tem como fazer. Ainda assim o
      // `expires` resolve o caso que mais pesa em qualquer navegador: fechar a
      // janela deixa de zerar a identidade.
      expires: 365,
    })
    return id
  }

  private restoreIdentity(): void {
    // Try the bare userId cookie first.
    const cookieUserId = Cookie.get(IDENTITY_COOKIE_NAME)
    if (cookieUserId) {
      this.userId = cookieUserId
    }

    // Primary source: the lean identity cookie (cross-subdomain).
    const stored = readIdentityCookie()
    if (stored) {
      this.hydrateFrom(stored)
      return
    }

    // Fallback: one-time migration from the legacy localStorage store. Rewrite
    // into the cookie so subsequent loads (and other subdomains) use it, then
    // drop the legacy key.
    const legacy = readLegacyLocalStorage()
    if (legacy) {
      this.hydrateFrom(legacy)
      // Legacy persisted the full traits object; promote its email/phone too.
      if (legacy.traits) {
        if (legacy.traits.email) this.traits.email = legacy.traits.email as string
        if (legacy.traits.phone) this.traits.phone = legacy.traits.phone as string
      }
      writeIdentityCookie({
        userId: this.userId ?? undefined,
        email: this.traits.email as string | undefined,
        phone: this.traits.phone as string | undefined,
      })
      clearLegacyLocalStorage()
    }
  }

  /** Apply a persisted snapshot onto the in-memory state. */
  private hydrateFrom(stored: PersistedIdentity): void {
    if (!this.userId && stored.userId) {
      this.userId = stored.userId
      // Recover the bare userId cookie if it went missing (Segment pattern).
      Cookie.set(IDENTITY_COOKIE_NAME, stored.userId, {
        secure: isSecureContext(),
        sameSite: 'Lax',
        path: '/',
        domain: getCookieDomain(),
        expires: 365,
      })
    }
    if (stored.email) this.traits.email = stored.email
    if (stored.phone) this.traits.phone = stored.phone
  }
}

let instance: IdentityStoreImpl | null = null

export function getIdentityStore(): IdentityStoreImpl {
  if (!instance) {
    instance = new IdentityStoreImpl()
  }
  return instance
}

/** @internal Reset singleton for testing */
export function _resetIdentityStore(): void {
  instance = null
}
